import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Eye } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PersonCell from '../../components/ui/PersonCell';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import Tabs from '../../components/ui/Tabs';
import ApprovalFlowTimeline from '../../components/ui/ApprovalFlowTimeline';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import api from '../../services/api';
import { getUserApprovalFlows, getApprovalFlowDetail } from '../../services/approvalFlowService';
import { formatDate } from '../../utils/Utils';

const Container = styled.div``;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 14px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 20px;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#ed6c0215'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#ed6c02'};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const InfoItem = styled.div``;

const InfoLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #A0A8B4;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionTitle = styled.h4`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #E8ECF0;
`;

const DescriptionText = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const RejectReasonBox = styled.div`
  background: #d32f2f08;
  border-left: 3px solid #d32f2f;
  padding: 12px 16px;
  border-radius: 0 4px 4px 0;
  margin-top: 8px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #E8ECF0;
`;

// BELGELER BÖLÜMÜ — desen İCAT EDİLMEDİ, en yakın kardeşten (ExpenseDetailPage'in
// ReceiptCard/ReceiptInfo/ReceiptName/ReceiptMeta/NoReceipts bloğu) birebir alındı:
// o da bir onay-detay sayfasıdır ve ekli fişi tam olarak böyle gösterir. Dosya
// özel sınıftadır (StoragePurposes.Private — purpose=leaveDocument ile yüklenir),
// ham adres DOM'a hiç verilmez; imzalı çözümü DocumentViewerModal `/v1/storage/view`
// üzerinden kendisi yapar (beş özlük-dosyası yüzeyiyle aynı sözleşme).
const DocumentCard = styled.div`
  border: 1.5px solid #E0E4E8;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: border-color 0.15s, box-shadow 0.15s;
  ${({ $clickable }) => $clickable && `
    &:hover {
      border-color: #0291EF;
      box-shadow: 0 2px 8px rgba(2, 145, 239, 0.1);
    }
  `}
`;

const DocumentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DocumentName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DocumentMeta = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #6b7280;
`;

const NoDocuments = styled.div`
  text-align: center;
  color: #A0A8B4;
  padding: 20px 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
`;

// "YÜKLENEMEDİ" ile "BOŞ" AYRI durumlardır ve bu bölümde ayrımı yutmak özellikle
// tehlikelidir: belge eki bazı izin türlerinde ZORUNLU tutuluyor (leave.documentRequired),
// dolayısıyla başarısız bir istekte "Belge Eklenmemiş" çizmek onaylayana ZORUNLU belgenin
// hiç eklenmediğini SÖYLEMEK olurdu. Ayrı bir görsel dil (uyarı kutusu) bilinçli.
const DocumentsLoadFailed = styled.div`
  background: #ed6c0208;
  border-left: 3px solid #ed6c02;
  padding: 12px 16px;
  border-radius: 0 4px 4px 0;
  margin-top: 8px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #8a4b00;
`;

// #72 — bkz. ExpenseDetailPage.js'in AYNI yorumu (birebir kopyalanmış çift):
// her iki override de kaldırıldı, Button.js'in "primary"/"danger"
// varyantları doğrudan kullanılıyor.

const RejectDialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const RejectDialogBox = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const RejectDialogTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

export default function LeaveDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();

  const [leave, setLeave] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('detail');
  const [flowSteps, setFlowSteps] = useState([]);
  // { status: 'loading' | 'ready' | 'failed', items: [] } — TEK state, çünkü
  // "yüklendi ama boş" ile "hiç yüklenemedi" ayrımı burada anlam taşıyor (aşağıdaki
  // DocumentsLoadFailed notu). `viewing`: ExpenseDetailPage ile aynı sözleşme —
  // KAYITLI depolama adresi, imzalı adres DEĞİL.
  const [documents, setDocuments] = useState({ status: 'loading', items: [] });
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/v1/leaves/${id}`)
      .then(async (res) => {
        const leaveData = res.data?.data;
        setLeave(leaveData);

        if (leaveData?.userId) {
          try {
            // Talep başladığı akışla biter (spec 2026-11-01 §4.4): sabitlenmiş izinde zincir O akıştan; işaretsiz eski
            // izinde sahibin bugünkü akışından.
            let flowId = null;
            if (leaveData.approvalFlowPinned) {
              flowId = leaveData.approvalFlowId || null;
            } else {
              const flowsRes = await getUserApprovalFlows(leaveData.userId);
              const flows = flowsRes.data?.data || [];
              flowId = flows.find((f) => f.flowType === 'leave')?.approvalFlowId || null;
            }
            if (flowId) {
              const flowDetail = await getApprovalFlowDetail(flowId);
              const steps = flowDetail.data?.data?.steps || [];
              setFlowSteps(steps.slice().sort((a, b) => a.stepOrder - b.stepOrder));
            }
          } catch { /* interceptor */ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Belgeler AYRI uçtan gelir (GET /v1/leaves/{id}/documents); izin detayının
  // gövdesinde DÖNMEZ. Uç bilinçli olarak modül kapısı TAŞIMAZ, kapsamı
  // LeaveAccessRules.CanAccessFormAsync kurar (sahip + İK + amir).
  //
  // api.js GET okumalarında REDDETMEZ (interceptor yalnız toast atar) — gerçek
  // sinyal `res.data.status`'tur. `.catch` yine de duruyor: ağ/iptal hatası
  // reddedebilir. İki yol da AYNI 'failed' durumuna düşer, 'ready + boş' ile
  // KARIŞTIRILMAZ.
  useEffect(() => {
    let iptalEdildi = false;
    setDocuments({ status: 'loading', items: [] });
    api.get(`/v1/leaves/${id}/documents`)
      .then((res) => {
        if (iptalEdildi) return;
        if (res?.data?.status === true) {
          setDocuments({ status: 'ready', items: res.data.data || [] });
        } else {
          setDocuments({ status: 'failed', items: [] });
        }
      })
      .catch(() => {
        if (!iptalEdildi) setDocuments({ status: 'failed', items: [] });
      });
    return () => { iptalEdildi = true; };
  }, [id]);

  const canApproveOrReject = leave?.status === 'waiting'
    && leave?.userId !== user?.id
    && leave?.isCurrentUserApprover === true;

  const handleApprove = useCallback(async () => {
    setProcessing(true);
    try {
      await api.put(`/v1/leaves/${id}/approve`);
      setLeave((prev) => prev ? { ...prev, status: 'approved' } : prev);
    } catch {
      // handled by interceptor
    } finally {
      setProcessing(false);
    }
  }, [id]);

  const handleReject = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    try {
      await api.put(`/v1/leaves/${id}/reject`, { managerNotes: rejectReason });
      setLeave((prev) => prev ? { ...prev, status: 'rejected', managerNotes: rejectReason } : prev);
      setRejectDialogOpen(false);
    } catch {
      // handled by interceptor
    } finally {
      setProcessing(false);
    }
  }, [id, rejectReason]);

  const statusLabels = {
    waiting: t('leave.waiting'),
    approved: t('leave.approved'),
    rejected: t('leave.rejected'),
    canceled: t('leave.canceled'),
  };

  if (loading) return null;
  if (!leave) return null;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('leave.myLeaves'), path: '/my-leaves' },
        { label: t('leave.detail') },
      ]} />

      <Card>
        <StatusBadge $status={leave.status}>
          {statusLabels[leave.status] || leave.status}
        </StatusBadge>

        <Tabs
          tabs={[
            { key: 'detail', label: t('leave.detailTab') },
            { key: 'flow', label: t('leave.approvalFlow') },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        {activeTab === 'flow' ? (
          <ApprovalFlowTimeline steps={flowSteps} source={leave} />
        ) : (
        <>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>{t('leave.employee')}</InfoLabel>
            <InfoValue>
              <PersonCell name={leave.userFullName} profilePicture={leave.userProfilePicture} />
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('leave.leaveType')}</InfoLabel>
            <InfoValue>{leave.leaveVariationName}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('leave.leaveStart')}</InfoLabel>
            <InfoValue>{formatDate(leave.leaveStart, i18n.language)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('leave.leaveEnd')}</InfoLabel>
            <InfoValue>{formatDate(leave.leaveEnd, i18n.language)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('leave.cost')}</InfoLabel>
            <InfoValue>{leave.cost}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('leave.approver')}</InfoLabel>
            <InfoValue>{leave.approverName || '-'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('leave.signatureStatus')}</InfoLabel>
            <InfoValue>{leave.signatureStatus || '-'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>{t('leave.createDate')}</InfoLabel>
            <InfoValue>{formatDate(leave.createDate, i18n.language)}</InfoValue>
          </InfoItem>
        </InfoGrid>

        {leave.userNotes && (
          <>
            <SectionTitle>{t('leave.userNotes')}</SectionTitle>
            <DescriptionText>{leave.userNotes}</DescriptionText>
          </>
        )}

        {leave.managerNotes && (
          <>
            <SectionTitle>{t('leave.managerNotes')}</SectionTitle>
            {leave.status === 'rejected' ? (
              <RejectReasonBox>
                <DescriptionText>{leave.managerNotes}</DescriptionText>
              </RejectReasonBox>
            ) : (
              <DescriptionText>{leave.managerNotes}</DescriptionText>
            )}
          </>
        )}

        {/* Dayanak belgeler — SALT GÖRÜNTÜLEME. Silme/ekleme uçları var ama bu
            yüzeyin işi onaylayanın belgeyi İNCELEMESİ; detay sayfasından belge
            yönetimi bilinçli olarak AÇILMADI. */}
        <SectionTitle>{t('leave.documents')}</SectionTitle>
        {documents.status === 'loading' ? null : documents.status === 'failed' ? (
          <DocumentsLoadFailed data-testid="leave-documents-failed">
            {t('leave.documentsLoadFailed')}
          </DocumentsLoadFailed>
        ) : documents.items.length === 0 ? (
          <NoDocuments data-testid="leave-documents-empty">{t('leave.noDocuments')}</NoDocuments>
        ) : (
          documents.items.map((belge) => (
            <DocumentCard
              key={belge.id}
              data-testid="leave-document-card"
              $clickable={!!belge.mediaUrl}
              onClick={() => belge.mediaUrl && setViewing({
                fileUrl: belge.mediaUrl,
                title: belge.name || t('leave.documentPreview'),
              })}
            >
              <DocumentInfo>
                <DocumentName>{belge.name || t('leave.document')}</DocumentName>
                <DocumentMeta>
                  {formatDate(belge.createDate, i18n.language)}
                  {belge.description ? ` - ${belge.description}` : ''}
                </DocumentMeta>
              </DocumentInfo>
              {belge.mediaUrl && <Eye size={16} color="#0291EF" />}
            </DocumentCard>
          ))
        )}

        {canApproveOrReject && (
          <ActionBar>
            <Button variant="danger" onClick={() => setRejectDialogOpen(true)} disabled={processing}>
              {t('leave.reject')}
            </Button>
            <Button onClick={handleApprove} disabled={processing}>
              {processing ? '...' : t('leave.approve')}
            </Button>
          </ActionBar>
        )}
        </>
        )}
      </Card>

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />

      {rejectDialogOpen && (
        <RejectDialogOverlay onClick={() => setRejectDialogOpen(false)}>
          <RejectDialogBox onClick={(e) => e.stopPropagation()}>
            <RejectDialogTitle>{t('leave.rejectTitle')}</RejectDialogTitle>
            <TextInput
              label={t('leave.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)} disabled={processing}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleReject} disabled={processing || !rejectReason.trim()}>
                {processing ? '...' : t('leave.reject')}
              </Button>
            </div>
          </RejectDialogBox>
        </RejectDialogOverlay>
      )}
    </Container>
  );
}
